import querystring from 'query-string';

/**
 * 获取 window.location 或 window.request.REQUEST（同构使用）
 * @category url 处理
 */
export const getLocation = () => {
  const loc = window.location
  if (loc.search === null) {
    loc.search = '';
  }
  if (loc.hash === null) {
    loc.hash = '';
  }
  return loc;
};

/**
 * 给链接（//开头）加上当前 protocol
 *
 * @export
 * @param {string} url
 * @category url 处理
 */
export const addProtocol = (url) => {
  if (/^\/\//.test(url)) {
    return getLocation().protocol + url;
  }
  return url;
};

/**
 * 根据字段获取 url 参数的值，优先取 query，如没有则取 hash
 * @param n url 参数
 * @returns 优先取 query，如没有则取 hash
 * @category url 处理
 */
export const getUrlParameter = (n) => {
  return getQuery(n) || getHash(n);
};

/**
 * 获取 query 对象，以键值对形式返回
 * @category url 处理
 */
export const getQueryObj = (url) => {
  if (typeof url === 'undefined') {
    return querystring.parse(getLocation().search ?? '');
  }

  const idx = url.indexOf('?');
  const hashIdx = url.indexOf('#');
  const query =
    idx === -1 ? '' : hashIdx === -1 ? url.slice(idx) : url.slice(idx, hashIdx);

  return querystring.parse(query);
};

/**
 * 获取 hash 对象，以键值对形式返回
 * @category url 处理
 */
export const getHashObj = (url) => {
  if (typeof url === 'undefined') {
    return querystring.parse(getLocation().hash ?? '');
  }

  const idx = url.indexOf('#');

  return querystring.parse(idx === -1 ? '' : url.slice(idx));
};

/**
 * 设置 hash 值。传入键值对对象，拼接成 url
 *
 * @param {({ [k: string]: string | number })} hashObject hashObject
 * @param {string} [link=''] url 链接。如不传，则当前页面hash立即改变生效；如传值，可从返回值拿到完整链接，业务再自行处理
 * @returns 拼接成的完整 url
 * @category url 处理
 */
export const setHash = (hashObject, link = '') => {
  let kvs = '';
  if (typeof hashObject === 'string') {
    kvs = hashObject;
  } else {
    const hashObj = { ...getHashObj(), ...hashObject };
    kvs = querystring.stringify(hashObj);
  }

  if (!link) {
    // 设置当前页面hash
    window.location.hash = `#${kvs}`;
  }

  let idx = link.indexOf('#');
  const seb = idx !== -1 ? '' : '#';
  idx = idx !== -1 ? idx + 1 : link.length;
  const url = link.substring(0, idx) + seb + kvs;

  return url;
};

/**
 * 设置 URL 中的查询参数
 *
 * @param obj 查询参数（对象形式）
 * @param options 修改选项
 * @param options.url 需要修改的 URL，不传则直接操作 window.location
 * @param options.isReplace 是否完全替换 URL 中的查询参数
 */
export const setUrlQuery = (
  obj,
  {
    url,
    isReplace,
    isPushState
  } = {}
) => {
  const location = getLocation();

  if (typeof url === 'undefined') {
    const { origin, pathname, hash } = location;
    const search = `?${querystring.stringify(
      isReplace ? obj : { ...getQueryObj(), ...obj }
    )}`;
    const newUrl = `${origin}${pathname}${search}${hash}`;

    if (window.history && typeof window.history.replaceState === 'function') {
      if (isPushState) {
        window.history.pushState({}, document.title, newUrl);
      } else {
        window.history.replaceState({}, document.title, newUrl);
      }
    } else {
      location.search = search;
    }

    return newUrl;
  }
  const queryIdx = url.indexOf('?');
  const hashIdx = url.indexOf('#');
  // eslint-disable-next-line no-nested-ternary
  const pathname =
    queryIdx !== -1
      ? url.substring(0, queryIdx)
      : hashIdx !== -1
      ? url.substring(0, hashIdx)
      : url;
  const search = `?${querystring.stringify(
    isReplace ? obj : { ...getQueryObj(url), ...obj }
  )}`;
  const hash =
    hashIdx === -1 ? '' : `#${querystring.stringify(getHashObj(url))}`;

  return `${pathname}${search}${hash}`;
};

/**
 * 根据字段获取url query参数的值
 * @param n url参数
 * @category url 处理
 */
export const getQuery = (n) => {
  const searchObj = getQueryObj();

  return searchObj[n] || '';
};

/**
 * 根据字段获取 url hash 的值
 * @param str 所需获取的字段
 * @returns
 * @category url 处理
 */
export const getHash = (str) => {
  const hashObj = getHashObj();

  return hashObj[str] || '';
};

/**
 * 判断屏幕是否处于横屏状态
 *
 * @returns
 * @category 屏幕相关
 */
export const isInLandscape = () => {
  if (window.matchMedia) {
    return (
      window.orientation >= 90 &&
      window.matchMedia('(orientation: landscape)').matches
    );
  }
  const { width, height } = getDeviceRect();

  // 不支持matchMedia的情况
  return width > height;
};

/**
 * 获取屏幕宽高
 *
 * @returns
 * @category 屏幕相关
 */
export const getDeviceRect = () => {
  const { innerWidth, innerHeight } = window;
  const { clientWidth = 0, clientHeight = 0 } = document.documentElement || {};

  const width = Math.min(innerWidth, clientWidth) || document.body.clientWidth;
  const height =
    Math.min(innerHeight, clientHeight) || document.body.clientHeight;

  return { width, height };
};

/**
 * base64转blob
 */
export const base64ToBlob = (dataURL) => {
  const arr = dataURL.split(',');
  const mime = arr?.[0].match(/:(.*?);/)?.[1];
  if (!mime) {
    return null;
  }
  const bstr = window.atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new Blob([u8arr], { type: mime });
};

/**
 * 转换链接为https协议
 *
 * @param {string} url
 */
export const httpsProtocol = (url) => {
  if (/^https:/.test(url)) {
    return url;
  }
  if (/^http:/.test(url)) {
    return url.replace('http', 'https');
  }
  if (/^\/\//.test(url)) {
    return `https:${url}`;
  }
  return url;
};
